import { useEffect, useState, useMemo } from "react";
import { Inbox, Trash2, Search, Check, RefreshCw, Reply } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export function MessagesManager({ onUnreadChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/messages");
      setMessages(r.data.messages || []);
      onUnreadChange?.(r.data.unread || 0);
    } catch {
      toast.error("Could not load messages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api
      .get("/admin/messages")
      .then((r) => {
        setMessages(r.data.messages || []);
        onUnreadChange?.(r.data.unread || 0);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return messages;
    return messages.filter((m) => `${m.name} ${m.email} ${m.message}`.toLowerCase().includes(s));
  }, [messages, q]);

  const unread = messages.filter((m) => !m.read).length;

  const markRead = async (m) => {
    try {
      await api.put(`/admin/messages/${m.id}/read`);
      const next = messages.map((x) => (x.id === m.id ? { ...x, read: true } : x));
      setMessages(next);
      onUnreadChange?.(next.filter((x) => !x.read).length);
    } catch {
      toast.error("Failed to update message");
    }
  };

  const remove = async (m) => {
    if (!window.confirm("Delete this message? This can't be undone.")) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      const next = messages.filter((x) => x.id !== m.id);
      setMessages(next);
      onUnreadChange?.(next.filter((x) => !x.read).length);
      toast.success("Message deleted");
    } catch {
      toast.error("Failed to delete message");
    }
  };

  return (
    <div data-testid="messages-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-[#1E63C4]" />
          <h3 className="font-heading font-bold text-lg">Messages</h3>
          {unread > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-[#E11D2A] text-white text-xs font-bold h-6 min-w-[24px] px-1.5" data-testid="messages-unread-count">
              {unread} new
            </span>
          )}
        </div>
        <button onClick={load} data-testid="messages-refresh" className="inline-flex items-center gap-1.5 text-sm text-[#555555] hover:text-[#1E63C4]">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="relative mb-5 max-w-md">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-[#999999]" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email or text…"
          data-testid="message-search"
          className="w-full rounded-md border border-[#E5E7EB] pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E63C4]"
        />
      </div>

      {loading ? (
        <p className="text-[#555555]">Loading messages…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-[#555555]" data-testid="messages-empty">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-[#CBD5E1]" />
          <p>{messages.length === 0 ? "No messages yet. Submissions from the Contact page will appear here." : "No messages match your search."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((m) => (
            <div
              key={m.id}
              data-testid={`message-card-${m.id}`}
              className={`rounded-lg border p-4 transition-colors ${m.read ? "border-[#E5E7EB] bg-white" : "border-[#1E63C4]/40 bg-blue-50/40"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {!m.read && <span className="h-2 w-2 rounded-full bg-[#1E63C4] shrink-0" aria-label="Unread" />}
                    <p className="font-semibold text-[#111111] truncate">{m.name}</p>
                  </div>
                  <a href={`mailto:${m.email}`} className="text-sm text-[#1E63C4] hover:underline break-all">{m.email}</a>
                </div>
                <span className="text-xs text-[#999999] shrink-0 whitespace-nowrap">
                  {new Date(m.created_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-3 text-sm text-[#333333] leading-relaxed whitespace-pre-line">{m.message}</p>
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <a
                  href={`mailto:${m.email}?subject=${encodeURIComponent("Re: your message to Quinte BCA Pool League")}`}
                  data-testid={`message-reply-${m.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#1E63C4] text-white rounded-md px-3 py-1.5 text-sm font-semibold hover:bg-[#174e9c]"
                >
                  <Reply className="h-4 w-4" /> Reply
                </a>
                {!m.read && (
                  <button
                    onClick={() => markRead(m)}
                    data-testid={`message-mark-read-${m.id}`}
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E5E7EB] px-3 py-1.5 text-sm font-medium text-[#555555] hover:border-[#1E63C4] hover:text-[#1E63C4]"
                  >
                    <Check className="h-4 w-4" /> Mark read
                  </button>
                )}
                <button
                  onClick={() => remove(m)}
                  data-testid={`message-delete-${m.id}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#E5E7EB] px-3 py-1.5 text-sm font-medium text-[#555555] hover:border-[#E11D2A] hover:text-[#E11D2A] ml-auto"
                >
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
